import type {
  ApiRequest,
  AuthType,
  BodyMode,
  HttpMethod,
  RawFormat,
  RequestBody,
  RequestBodyField,
  RequestHeader,
  RequestParam,
  RequestVariable,
} from '@core/entities/ApiRequest';
import type { HistoryEntry } from '@core/entities/History';
import type {
  ApiRequestDocumentPayload,
  ApiRequestRepository,
  CreateApiRequestPayload,
  ParsedCurlRequest,
} from '@core/repositories/ApiRequestRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';
import { toHistoryEntry, type HistoryResponse } from '@infrastructure/mappers/toHistoryEntry';
import {
  toRequestSettings,
  toRequestSettingsPayload,
  type RequestSettingsResponse,
} from '@infrastructure/mappers/requestSettingsMapper';

interface RequestParamResponse {
  id: string;
  type: 'query' | 'path';
  key: string;
  value: string | null;
  description: string | null;
  is_enabled: boolean;
  position: number;
}

interface RequestHeaderResponse {
  id: string;
  key: string;
  value: string | null;
  description: string | null;
  is_enabled: boolean;
  position: number;
}

interface RequestVariableResponse {
  id: string;
  key: string;
  value: string | null;
  type: 'default' | 'secret';
  is_enabled: boolean;
  position: number;
}

interface RequestBodyFieldResponse {
  id: string;
  key: string;
  value: string | null;
  type: 'text' | 'file';
  file_path: string | null;
  is_enabled: boolean;
  position: number;
}

interface RequestBodyResponse {
  mode: BodyMode;
  raw_format: RawFormat | null;
  raw_content: string | null;
  graphql_query: string | null;
  graphql_variables: Record<string, unknown> | null;
  binary_file_path: string | null;
  fields?: RequestBodyFieldResponse[];
}

interface ApiRequestResponse {
  id: string;
  collection_id: string;
  folder_id?: string | null;
  name: string;
  method: HttpMethod;
  url: string;
  description: string | null;
  position: number;
  auth_type: AuthType;
  auth_config: Record<string, unknown> | null;
  pre_request_script: string | null;
  test_script: string | null;
  settings?: RequestSettingsResponse | null;
  created_by?: { id: string; name: string } | null;
  params?: RequestParamResponse[];
  headers?: RequestHeaderResponse[];
  body?: RequestBodyResponse | null;
  variables?: RequestVariableResponse[];
  is_favorited: boolean;
  abilities: { view: boolean; update: boolean; delete: boolean; execute: boolean };
  created_at: string;
  updated_at: string;
}

const toParam = (payload: RequestParamResponse): RequestParam => ({
  id: payload.id,
  type: payload.type,
  key: payload.key,
  value: payload.value,
  description: payload.description,
  isEnabled: payload.is_enabled,
  position: payload.position,
});

const toHeader = (payload: RequestHeaderResponse): RequestHeader => ({
  id: payload.id,
  key: payload.key,
  value: payload.value,
  description: payload.description,
  isEnabled: payload.is_enabled,
  position: payload.position,
});

const toRequestVariable = (payload: RequestVariableResponse): RequestVariable => ({
  id: payload.id,
  key: payload.key,
  value: payload.value,
  type: payload.type,
  isEnabled: payload.is_enabled,
  position: payload.position,
});

const toBodyField = (payload: RequestBodyFieldResponse): RequestBodyField => ({
  id: payload.id,
  key: payload.key,
  value: payload.value,
  type: payload.type,
  filePath: payload.file_path,
  isEnabled: payload.is_enabled,
  position: payload.position,
});

const toBody = (payload: RequestBodyResponse): RequestBody => ({
  mode: payload.mode,
  rawFormat: payload.raw_format,
  rawContent: payload.raw_content,
  graphqlQuery: payload.graphql_query,
  graphqlVariables: payload.graphql_variables,
  binaryFilePath: payload.binary_file_path,
  fields: (payload.fields ?? []).map(toBodyField),
});

interface ParsedCurlHeaderResponse {
  key: string;
  value: string | null;
  description: string | null;
  is_enabled: boolean;
}

interface ParsedCurlBodyResponse {
  mode: BodyMode;
  raw_format: RawFormat | null;
  raw_content: string | null;
  graphql_query: string | null;
  graphql_variables: Record<string, unknown> | null;
  binary_file_path: string | null;
  fields?: {
    key: string;
    value: string | null;
    type: 'text' | 'file';
    file_path: string | null;
    is_enabled: boolean;
  }[];
}

interface ParsedCurlResponse {
  name: string;
  method: HttpMethod;
  url: string;
  auth_type: AuthType;
  auth_config: Record<string, unknown> | null;
  headers: ParsedCurlHeaderResponse[];
  body: ParsedCurlBodyResponse | null;
}

const toParsedCurlRequest = (payload: ParsedCurlResponse): ParsedCurlRequest => ({
  name: payload.name,
  method: payload.method,
  url: payload.url,
  authType: payload.auth_type,
  authConfig: payload.auth_config,
  headers: payload.headers.map((header) => ({
    key: header.key,
    value: header.value,
    description: header.description,
    isEnabled: header.is_enabled,
  })),
  body: payload.body
    ? {
        mode: payload.body.mode,
        rawFormat: payload.body.raw_format,
        rawContent: payload.body.raw_content,
        graphqlQuery: payload.body.graphql_query,
        graphqlVariables: payload.body.graphql_variables,
        binaryFilePath: payload.body.binary_file_path,
        fields: (payload.body.fields ?? []).map((field) => ({
          key: field.key,
          value: field.value,
          type: field.type,
          filePath: field.file_path,
          isEnabled: field.is_enabled,
        })),
      }
    : null,
});

const toApiRequest = (payload: ApiRequestResponse): ApiRequest => ({
  id: payload.id,
  collectionId: payload.collection_id,
  folderId: payload.folder_id ?? null,
  name: payload.name,
  method: payload.method,
  url: payload.url,
  description: payload.description,
  position: payload.position,
  authType: payload.auth_type,
  authConfig: payload.auth_config,
  preRequestScript: payload.pre_request_script,
  testScript: payload.test_script,
  settings: toRequestSettings(payload.settings),
  createdBy: payload.created_by ?? null,
  params: (payload.params ?? []).map(toParam),
  headers: (payload.headers ?? []).map(toHeader),
  body: payload.body ? toBody(payload.body) : null,
  variables: (payload.variables ?? []).map(toRequestVariable),
  isFavorited: payload.is_favorited,
  abilities: payload.abilities,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

const toDocumentBody = (payload: ApiRequestDocumentPayload): Record<string, unknown> => {
  const body: Record<string, unknown> = {};

  if (payload.name !== undefined) body.name = payload.name;
  if (payload.method !== undefined) body.method = payload.method;
  if (payload.url !== undefined) body.url = payload.url;
  if (payload.description !== undefined) body.description = payload.description;
  if (payload.authType !== undefined) body.auth_type = payload.authType;
  if (payload.authConfig !== undefined) body.auth_config = payload.authConfig;
  if (payload.preRequestScript !== undefined) body.pre_request_script = payload.preRequestScript;
  if (payload.testScript !== undefined) body.test_script = payload.testScript;
  if (payload.settings !== undefined) body.settings = toRequestSettingsPayload(payload.settings);

  if (payload.params !== undefined) {
    body.params = payload.params.map((param) => ({
      type: param.type ?? 'query',
      key: param.key,
      value: param.value ?? null,
      description: param.description ?? null,
      is_enabled: param.isEnabled ?? true,
    }));
  }

  if (payload.headers !== undefined) {
    body.headers = payload.headers.map((header) => ({
      key: header.key,
      value: header.value ?? null,
      description: header.description ?? null,
      is_enabled: header.isEnabled ?? true,
    }));
  }

  if (payload.variables !== undefined) {
    body.variables = payload.variables.map((variable) => ({
      key: variable.key,
      value: variable.value ?? null,
      type: variable.type ?? 'default',
      is_enabled: variable.isEnabled ?? true,
    }));
  }

  if (payload.body !== undefined) {
    body.body =
      payload.body === null
        ? null
        : {
            mode: payload.body.mode,
            raw_format: payload.body.rawFormat ?? null,
            raw_content: payload.body.rawContent ?? null,
            graphql_query: payload.body.graphqlQuery ?? null,
            graphql_variables: payload.body.graphqlVariables ?? null,
            binary_file_path: payload.body.binaryFilePath ?? null,
            fields: (payload.body.fields ?? []).map((field) => ({
              key: field.key,
              value: field.value ?? null,
              type: field.type ?? 'text',
              file_path: field.filePath ?? null,
              is_enabled: field.isEnabled ?? true,
            })),
          };
  }

  return body;
};

export class ApiRequestRepositoryImpl implements ApiRequestRepository {
  async list(collectionId: string, folderId?: string | null): Promise<ApiRequest[]> {
    try {
      const response = await httpClient.get<{ data: ApiRequestResponse[] }>(
        `/collections/${collectionId}/requests`,
        { params: folderId ? { folder_uuid: folderId } : undefined },
      );
      return response.data.data.map(toApiRequest);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async listAll(collectionId: string): Promise<ApiRequest[]> {
    try {
      const response = await httpClient.get<{ data: ApiRequestResponse[] }>(
        `/collections/${collectionId}/requests/all`,
      );
      return response.data.data.map(toApiRequest);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<ApiRequest> {
    try {
      const response = await httpClient.get<{ data: ApiRequestResponse }>(`/requests/${id}`);
      return toApiRequest(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async parseCurl(command: string): Promise<ParsedCurlRequest> {
    try {
      const response = await httpClient.post<{ data: ParsedCurlResponse }>('/curl/parse', {
        command,
      });
      return toParsedCurlRequest(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(collectionId: string, payload: CreateApiRequestPayload): Promise<ApiRequest> {
    try {
      const response = await httpClient.post<{ data: ApiRequestResponse }>(
        `/collections/${collectionId}/requests`,
        { ...toDocumentBody(payload), folder_uuid: payload.folderId },
      );
      return toApiRequest(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: ApiRequestDocumentPayload): Promise<ApiRequest> {
    try {
      const response = await httpClient.put<{ data: ApiRequestResponse }>(
        `/requests/${id}`,
        toDocumentBody(payload),
      );
      return toApiRequest(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async duplicate(id: string): Promise<ApiRequest> {
    try {
      const response = await httpClient.post<{ data: ApiRequestResponse }>(
        `/requests/${id}/duplicate`,
      );
      return toApiRequest(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async move(id: string, folderId: string | null, position: number): Promise<ApiRequest> {
    try {
      const response = await httpClient.post<{ data: ApiRequestResponse }>(`/requests/${id}/move`, {
        folder_uuid: folderId,
        position,
      });
      return toApiRequest(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async reorder(collectionId: string, orderedIds: string[]): Promise<void> {
    try {
      await httpClient.post(`/collections/${collectionId}/requests/reorder`, { ids: orderedIds });
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/requests/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async favorite(id: string): Promise<void> {
    try {
      await httpClient.post(`/requests/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async unfavorite(id: string): Promise<void> {
    try {
      await httpClient.delete(`/requests/${id}/favorite`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async send(id: string, overrides?: ApiRequestDocumentPayload): Promise<HistoryEntry> {
    try {
      const response = await httpClient.post<{ data: HistoryResponse }>(
        `/requests/${id}/send`,
        overrides ? toDocumentBody(overrides) : {},
      );
      return toHistoryEntry(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
